/**
 * JSON field component
 * JSON editor with syntax highlighting and validation
 */

import React, { useState, useCallback, useMemo } from "react";
import {
  FieldSchema,
  ValidationResult,
  getHelpText,
} from "@/config/fieldConfig";
import { useI18n } from "@/i18n";
import { useAvailableTools } from "@/features/tools/hooks/useTools";
import { useAvailableMCPServers } from "@/features/tools/hooks/useTools";
import type { BuiltinToolkit, MCPTool } from "@/types/tools";

export interface JsonFieldProps {
  name: string;
  value: string | object | any[] | null;
  onChange: (value: any) => void;
  schema: FieldSchema;
  error?: ValidationResult;
  disabled?: boolean;
  onBrowseTools?: () => void;
}

export const JsonField: React.FC<JsonFieldProps> = ({
  name,
  value,
  onChange,
  schema,
  error,
  disabled = false,
  onBrowseTools,
}) => {
  const { messages } = useI18n();
  const tActions = messages.agentConfig.actions as
    | Record<string, string>
    | undefined;
  const tTools = messages.agentConfig.toolsBrowser;
  const [parseError, setParseError] = useState<string | null>(null);
  const label =
    typeof schema.label === "function" ? schema.label() : schema.label;
  const placeholder =
    typeof schema.placeholder === "function"
      ? schema.placeholder()
      : schema.placeholder;
  const help = getHelpText(schema.help);

  // Fetch tools data for display enrichment (only for tool fields)
  const { data: toolsData } = useAvailableTools();
  const { data: mcpData } = useAvailableMCPServers();

  // Build lookup maps for tools metadata
  const builtinToolkitsMap = useMemo(() => {
    const map: Record<string, BuiltinToolkit> = {};
    if (toolsData?.builtin_toolkits) {
      toolsData.builtin_toolkits.forEach((toolkit) => {
        map[toolkit.name] = toolkit;
      });
    }
    return map;
  }, [toolsData]);

  // Build lookup map for sub-tools display names (e.g., "calculator.add" -> "加法计算")
  // Only store entries where display_name differs from raw name (meaning real enhancement exists)
  const subToolsMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (toolsData?.builtin_toolkits) {
      toolsData.builtin_toolkits.forEach((toolkit) => {
        toolkit.tools?.forEach((tool) => {
          // Only add to map if display_name is different from the raw tool name
          // (meaning there's a real enhancement, not just the default)
          if (tool.display_name && tool.display_name !== tool.name) {
            map[tool.full_name] = tool.display_name;
          }
        });
      });
    }
    return map;
  }, [toolsData]);

  const customToolsMap = useMemo(() => {
    const map: Record<string, { category: string; displayName: string }> = {};
    if (toolsData?.custom_tools) {
      toolsData.custom_tools.forEach((tool) => {
        map[tool.name] = {
          category: tool.category || "other",
          displayName: tool.display_name || tool.name,
        };
      });
    }
    return map;
  }, [toolsData]);

  const mcpToolsMap = useMemo(() => {
    const map: Record<string, MCPTool[]> = {};
    if (mcpData?.mcp_tools) {
      mcpData.mcp_tools.forEach((tool) => {
        if (!map[tool.server_id]) {
          map[tool.server_id] = [];
        }
        map[tool.server_id].push(tool);
      });
    }
    return map;
  }, [mcpData]);

  const borderClass =
    (error || parseError) && !error?.isWarning
      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
      : error?.isWarning
      ? "border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500"
      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500";

  // Handle JSON input change with validation
  const handleChange = useCallback(
    (newValue: string) => {
      onChange(newValue);

      // Validate JSON syntax
      if (newValue && newValue.trim()) {
        try {
          JSON.parse(newValue);
          setParseError(null);
        } catch (e: any) {
          setParseError(`Invalid JSON: ${e.message}`);
        }
      } else {
        setParseError(null);
      }
    },
    [onChange]
  );

  // Format JSON with pretty print
  const formatJson = useCallback(() => {
    if (!value) return;

    try {
      // Handle both string and already-parsed values
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      const formatted = JSON.stringify(parsed, null, 2);
      onChange(formatted);
      setParseError(null);
    } catch (e: any) {
      setParseError(`Cannot format: ${e.message}`);
    }
  }, [value, onChange]);

  // Render a chip/badge for tool items
  const renderChip = (
    text: string,
    variant: "primary" | "secondary" | "muted" | "invalid" = "primary",
    key?: string,
    title?: string
  ) => {
    const baseClasses =
      "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mr-1.5 mb-1.5";
    const variantClasses = {
      primary: "bg-blue-100 text-blue-800",
      secondary: "bg-green-100 text-green-800",
      muted: "bg-gray-100 text-gray-700",
      invalid: "bg-red-100 text-red-800 line-through",
    };
    return (
      <span
        key={key || text}
        className={`${baseClasses} ${variantClasses[variant]}`}
        title={title}
      >
        {text}
        {variant === "invalid" && (
          <span className="ml-1 text-red-600">✕</span>
        )}
      </span>
    );
  };

  // Helper to extract toolkit short name from full toolkit name
  // e.g., "builtin:reasoning" -> "reasoning", "builtin:web_search.baidu_search" -> "web_search.baidu_search"
  const getToolkitShortName = (toolkit: string): string => {
    return toolkit.replace(/^builtin:/, "");
  };

  // Helper to extract parent from tool full_name
  // e.g., "reasoning.think" -> "reasoning", "calculator.add" -> "calculator"
  const getToolParent = (fullName: string): string => {
    const dotIndex = fullName.indexOf(".");
    return dotIndex > -1 ? fullName.substring(0, dotIndex) : fullName;
  };

  // Helper to extract tool name from full_name
  // e.g., "reasoning.think" -> "think", "calculator.add" -> "add"
  const getToolName = (fullName: string): string => {
    const dotIndex = fullName.indexOf(".");
    return dotIndex > -1 ? fullName.substring(dotIndex + 1) : fullName;
  };

  // Helper to extract server name from MCP tool namespaced_name
  // e.g., "bazi_mcp___calculate_bazi_tool" -> "bazi_mcp"
  const getMcpToolServer = (namespacedName: string): string => {
    const separatorIndex = namespacedName.indexOf("___");
    return separatorIndex > -1
      ? namespacedName.substring(0, separatorIndex)
      : namespacedName;
  };

  // Helper to extract tool name from MCP tool namespaced_name
  // e.g., "bazi_mcp___calculate_bazi_tool" -> "calculate_bazi_tool"
  const getMcpToolName = (namespacedName: string): string => {
    const separatorIndex = namespacedName.indexOf("___");
    return separatorIndex > -1
      ? namespacedName.substring(separatorIndex + 3)
      : namespacedName;
  };

  // Render tools configuration with category grouping
  const renderToolsConfig = useCallback((
    parsed: any,
    fieldName: string
  ): React.ReactNode => {
    // Check if this is a builtin_tools config: { toolkits: [...], enabled_tools?: [...] }
    if (
      fieldName === "builtin_tools" &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      parsed.toolkits
    ) {
      const toolkits = parsed.toolkits as string[];
      const enabledTools = (parsed.enabled_tools as string[] | undefined) || [];

      // Group enabled_tools by their parent toolkit
      const toolsByParent: Record<string, string[]> = {};
      enabledTools.forEach((tool) => {
        const parent = getToolParent(tool);
        if (!toolsByParent[parent]) {
          toolsByParent[parent] = [];
        }
        toolsByParent[parent].push(tool);
      });

      // Group toolkits by category
      const toolkitsByCategory: Record<
        string,
        Array<{
          name: string;
          displayName: string;
          childTools: string[];
          isValid: boolean;
        }>
      > = {};

      toolkits.forEach((toolkit) => {
        const toolkitInfo = builtinToolkitsMap[toolkit];
        const isValid = !!toolkitInfo;
        const category = toolkitInfo?.category || "other";
        const displayName = toolkitInfo?.display_name || toolkit;
        const shortName = getToolkitShortName(toolkit);
        const childTools = toolsByParent[shortName] || [];

        if (!toolkitsByCategory[category]) {
          toolkitsByCategory[category] = [];
        }
        toolkitsByCategory[category].push({
          name: toolkit,
          displayName,
          childTools,
          isValid,
        });
      });

      const categoryOrder = [
        "astrology",
        "search",
        "utility",
        "reasoning",
        "other",
      ];
      const sortedCategories = Object.keys(toolkitsByCategory).sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a);
        const bIndex = categoryOrder.indexOf(b);
        if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });

      return (
        <div className="space-y-3">
          {sortedCategories.map((category) => (
            <div key={category}>
              <div className="text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
                {category}
              </div>
              <div className="space-y-1.5">
                {toolkitsByCategory[category].map(
                  ({ name, displayName, childTools, isValid }) => {
                    const hasSelectedSubTools = childTools.length > 0;

                    return (
                      <div
                        key={name}
                        className={`border rounded-lg p-2 bg-white ${
                          isValid ? "border-gray-200" : "border-red-300 bg-red-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {renderChip(
                            displayName,
                            isValid ? "primary" : "invalid",
                            undefined,
                            isValid ? undefined : tTools?.toolNotFound || "Toolkit not found - please remove"
                          )}
                          {isValid && !hasSelectedSubTools && (
                            <span className="text-xs text-gray-400 italic">
                              {tTools?.allToolsEnabled || "(all tools enabled)"}
                            </span>
                          )}
                        </div>
                        {hasSelectedSubTools && (
                          <div className="ml-4 mt-1.5 flex flex-wrap">
                            {childTools.map((tool) =>
                              renderChip(
                                subToolsMap[tool] || getToolName(tool),
                                "secondary",
                                tool
                              )
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Check if this is an mcp_servers config: { servers: [...], enabled_tools?: [...] }
    if (
      fieldName === "mcp_servers" &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      parsed.servers
    ) {
      const servers = parsed.servers as string[];
      const enabledTools = (parsed.enabled_tools as string[] | undefined) || [];

      // Build a set of valid server IDs from mcpToolsMap
      const validServerIds = new Set(Object.keys(mcpToolsMap));

      // Group enabled_tools by their server
      const toolsByServer: Record<string, string[]> = {};
      enabledTools.forEach((tool) => {
        const server = getMcpToolServer(tool);
        if (!toolsByServer[server]) {
          toolsByServer[server] = [];
        }
        toolsByServer[server].push(tool);
      });

      // Normalize server names for matching (replace - with _)
      const normalizeServerName = (name: string) => name.replace(/-/g, "_");

      return (
        <div className="space-y-2">
          {servers.map((server) => {
            const normalizedServer = normalizeServerName(server);
            const childTools = toolsByServer[normalizedServer] || [];
            const hasSelectedTools = childTools.length > 0;
            // Check if server exists (try both original and normalized names)
            const isValid = validServerIds.has(server) || validServerIds.has(normalizedServer);

            return (
              <div
                key={server}
                className={`border rounded-lg p-2 bg-white ${
                  isValid ? "border-gray-200" : "border-red-300 bg-red-50"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {renderChip(
                    server,
                    isValid ? "primary" : "invalid",
                    undefined,
                    isValid ? undefined : tTools?.serverNotFound || "Server not found - please remove"
                  )}
                  {isValid && !hasSelectedTools && (
                    <span className="text-xs text-gray-400 italic">
                      {tTools?.allToolsEnabled || "(all tools enabled)"}
                    </span>
                  )}
                </div>
                {hasSelectedTools && (
                  <div className="ml-4 mt-1 flex flex-wrap">
                    {childTools.map((tool) =>
                      renderChip(getMcpToolName(tool), "secondary", tool)
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Check if this is a custom_tools config: { modules: [...], enabled_tools?: [...] }
    if (
      fieldName === "custom_tools" &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      parsed.modules
    ) {
      const modules = parsed.modules as string[];
      const enabledTools = (parsed.enabled_tools as string[] | undefined) || [];

      return (
        <div className="space-y-2">
          {modules.map((module) => {
            // For custom tools, we don't have the same parent-child structure
            // Just show the module name
            return (
              <div
                key={module}
                className="border border-gray-200 rounded-lg p-2 bg-white"
              >
                {renderChip(module, "primary")}
              </div>
            );
          })}
          {enabledTools.length > 0 && (
            <div className="border border-gray-200 rounded-lg p-2 bg-white">
              <div className="text-xs font-medium text-gray-500 mb-1.5">
                {tTools?.enabledTools || "Enabled Tools"}
              </div>
              <div className="flex flex-wrap">
                {enabledTools.map((t) => renderChip(t, "secondary"))}
              </div>
            </div>
          )}
        </div>
      );
    }

    // Check if this is a custom_tools array (simple list of tool names)
    if (fieldName === "custom_tools" && Array.isArray(parsed)) {
      const tools = parsed as string[];
      if (tools.length === 0) {
        return null; // Let the default empty handling take over
      }

      // Separate builtin tools from custom tools, track invalid ones
      const builtinTools: Array<{
        name: string;
        displayName: string;
        isValid: boolean;
      }> = [];
      const toolsByCategory: Record<
        string,
        Array<{ name: string; displayName: string; isValid: boolean }>
      > = {};

      tools.forEach((toolName) => {
        // Check if it's a builtin tool (starts with "builtin:")
        if (toolName.startsWith("builtin:")) {
          const toolkitInfo = builtinToolkitsMap[toolName];
          const displayName = toolkitInfo?.display_name || toolName;
          const isValid = !!toolkitInfo;
          builtinTools.push({ name: toolName, displayName, isValid });
        } else {
          // It's a custom tool
          const toolInfo = customToolsMap[toolName];
          const isValid = !!toolInfo;
          const category = toolInfo?.category || "other";
          const displayName = toolInfo?.displayName || toolName;

          if (!toolsByCategory[category]) {
            toolsByCategory[category] = [];
          }
          toolsByCategory[category].push({ name: toolName, displayName, isValid });
        }
      });

      const categoryOrder = [
        "astrology",
        "birth_chart",
        "search",
        "utility",
        "other",
      ];
      const sortedCategories = Object.keys(toolsByCategory).sort((a, b) => {
        const aIndex = categoryOrder.indexOf(a);
        const bIndex = categoryOrder.indexOf(b);
        if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });

      return (
        <div className="space-y-3">
          {/* Builtin tools section (legacy - should be migrated) */}
          {builtinTools.length > 0 && (
            <div>
              <div className="text-xs font-medium text-orange-600 mb-1.5 uppercase tracking-wide">
                ⚠️ {tTools?.builtinToolkits || "Builtin Toolkits"}{" "}
                <span className="normal-case font-normal">
                  ({tTools?.willMigrateOnSave || "will migrate on save"})
                </span>
              </div>
              <div className="border border-orange-200 rounded-lg p-2 bg-orange-50">
                <div className="flex flex-wrap">
                  {builtinTools.map(({ name, displayName, isValid }) =>
                    renderChip(
                      displayName,
                      isValid ? "primary" : "invalid",
                      name,
                      isValid
                        ? undefined
                        : tTools?.toolNotFound || "Tool not found - please remove"
                    )
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Custom tools by category */}
          {sortedCategories.map((category) => (
            <div key={category}>
              <div className="text-xs font-medium text-gray-500 mb-1.5 uppercase tracking-wide">
                {category}
              </div>
              <div className="border border-gray-200 rounded-lg p-2 bg-white">
                <div className="flex flex-wrap">
                  {toolsByCategory[category].map(
                    ({ name, displayName, isValid }) =>
                      renderChip(
                        displayName,
                        isValid ? "muted" : "invalid",
                        name,
                        isValid
                          ? undefined
                          : tTools?.toolNotFound ||
                              "Tool not found - please remove"
                      )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return null;
  }, [builtinToolkitsMap, subToolsMap, mcpToolsMap, customToolsMap, tTools]);

  // Render as list (for display mode)
  const renderAsList = useCallback(() => {
    if (!value) return null;

    try {
      // Handle both string (JSON) and already-parsed values
      let parsed = value;
      if (typeof value === "string") {
        parsed = JSON.parse(value);
      }

      // Try to render as tools config first
      const toolsDisplay = renderToolsConfig(parsed, name);
      if (toolsDisplay) {
        return toolsDisplay;
      }

      // Array of strings - render as chips
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) {
          return (
            <p className="text-sm text-gray-500 italic">
              {tTools?.noItemsConfigured || "No items configured"}
            </p>
          );
        }
        return (
          <div className="flex flex-wrap">
            {parsed.map((item, index) =>
              typeof item === "string" ? (
                renderChip(item, "muted")
              ) : (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mr-1.5 mb-1.5 bg-gray-100 text-gray-700"
                >
                  {JSON.stringify(item)}
                </span>
              )
            )}
          </div>
        );
      }

      // Generic object (fallback)
      if (typeof parsed === "object" && parsed !== null) {
        const entries = Object.entries(parsed);
        if (entries.length === 0) {
          return (
            <p className="text-sm text-gray-500 italic">
              {tTools?.noItemsConfigured || "No items configured"}
            </p>
          );
        }
        return (
          <dl className="space-y-2">
            {entries.map(([key, val]) => (
              <div key={key}>
                <dt className="text-xs font-medium text-gray-500 mb-1">
                  {key}
                </dt>
                <dd className="text-sm text-gray-700">
                  {Array.isArray(val) ? (
                    <div className="flex flex-wrap">
                      {(val as any[]).map((item, i) =>
                        typeof item === "string" ? (
                          renderChip(item, "muted")
                        ) : (
                          <span
                            key={i}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mr-1.5 mb-1.5 bg-gray-100 text-gray-700"
                          >
                            {JSON.stringify(item)}
                          </span>
                        )
                      )}
                    </div>
                  ) : typeof val === "string" ? (
                    val
                  ) : (
                    JSON.stringify(val)
                  )}
                </dd>
              </div>
            ))}
          </dl>
        );
      }

      // String
      return <p className="text-sm text-gray-700">{String(parsed)}</p>;
    } catch {
      return null;
    }
  }, [value, name, tTools, renderToolsConfig]);

  // Check if we should show as list
  // value can be string, array, or object
  const hasValue =
    value !== null &&
    value !== undefined &&
    (typeof value !== "string" || value.trim() !== "");
  const showAsList = schema.displayMode === "list" && hasValue && !parseError;

  return (
    <div className="field-wrapper">
      <div className="flex items-center justify-between mb-1">
        <label
          htmlFor={`field-${name}`}
          className="block text-sm font-medium text-gray-700"
        >
          {label}
          {schema.required && <span className="text-red-500 ml-1">*</span>}
        </label>

        <div className="flex gap-2">
          {!schema.readonly && value && (
            <button
              type="button"
              onClick={formatJson}
              disabled={disabled || !!parseError}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {tTools?.format || "Format"}
            </button>
          )}

          {schema.hasToolBrowser && onBrowseTools && (
            <button
              type="button"
              onClick={onBrowseTools}
              disabled={disabled}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {tActions?.browseTools || "Browse Tools"}
            </button>
          )}
        </div>
      </div>

      {showAsList ? (
        <div className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-gray-50 min-h-[100px]">
          {renderAsList()}
        </div>
      ) : (
        <textarea
          id={`field-${name}`}
          value={
            typeof value === "string"
              ? value
              : value
              ? JSON.stringify(value, null, 2)
              : ""
          }
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={schema.readonly}
          rows={10}
          className={`
            w-full px-3 py-2 rounded-lg border
            focus:outline-none focus:ring-2
            disabled:bg-gray-100 disabled:cursor-not-allowed
            read-only:bg-gray-50 read-only:cursor-not-allowed
            resize-y font-mono text-sm
            ${borderClass}
          `}
        />
      )}

      {(error || parseError) && (
        <div
          className={`text-xs mt-1 ${
            error?.isWarning ? "text-yellow-600" : "text-red-600"
          }`}
        >
          {parseError || error?.message}
        </div>
      )}

      {help && !error && !parseError && (
        <div className="text-xs text-gray-500 mt-1">{help}</div>
      )}
    </div>
  );
};

export default JsonField;
